// Parsing da planilha histórica de cancelamentos ("SOLICITAÇÃO DOS CANCELAMENTOS.xlsx").
//
// Tudo aqui é PURO (sem I/O, sem Supabase, sem xlsx): recebe matriz de células
// já lida e devolve registros canônicos + diagnóstico. É assim que dá pra
// cobrir com `node --test` sem subir nada — mesma restrição de import
// documentada em src/lib/cancelamentos.ts (nada de import estático de valor
// que dependa do alias do Vite).
//
// A planilha real tem 17 abas com layout DIFERENTE em cada mês (13 a 21
// colunas, cabeçalhos com erro de digitação e repetidos). Por isso o
// casamento de coluna é por palavra-chave normalizada, não por posição.

import type { MotivoCancelamento, StatusSolicitacaoCancelamento } from './cancelamentos';

/** Colunas do modelo canônico — mesma ordem usada na exportação. */
export const COLUNAS_MODELO = [
  'UC', 'CPF/CNPJ', 'Cliente', 'Data da Solicitação', 'Motivo (categoria)', 'Detalhe do Motivo',
  'Vendedor Responsável', 'Distrato', 'Nº / Data do Termo', 'Saldo (kWh)', 'Débitos em Aberto (R$)',
  'Valor da Multa (R$)', 'Multa — Situação', 'Data de Retirada do Rateio', 'Status', 'Observação',
] as const;

export type DistratoInformado = 'sim' | 'nao' | 'aguardando';

export interface LinhaPlanilha {
  aba: string;
  /** 1-based, como aparece no Excel (inclui a linha de cabeçalho). */
  linha: number;
  ucBruta: string;
  ucNormalizada: string;
  cpfCnpj: string | null;
  cliente: string;
  dataSolicitacao: string | null;
  dataAmbigua: boolean;
  motivoTexto: string | null;
  motivoCategoria: MotivoCancelamento;
  motivoConfianca: 'alta' | 'baixa';
  vendedorNome: string | null;
  distrato: DistratoInformado | null;
  distratoData: string | null;
  distratoRef: string | null;
  saldoKwh: number | null;
  debitosValor: number | null;
  multaValor: number | null;
  multaSituacao: string | null;
  dataRetiradaRateio: string | null;
  status: StatusSolicitacaoCancelamento;
  observacao: string | null;
  /** Problemas que não impedem a linha de existir, mas que a tela precisa mostrar. */
  avisos: string[];
}

const norm = (v: unknown): string => String(v ?? '').replace(/\s+/g, ' ').trim();
const semAcento = (v: string) => v.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
const chave = (v: unknown) => semAcento(norm(v)).toUpperCase();

/** Só dígitos — a planilha traz UC com ponto, espaço e apóstrofo de Excel. */
export function normalizarUcTexto(v: unknown): string {
  return String(v ?? '').replace(/\D/g, '').replace(/^0+/, '');
}

export function normalizarCpfCnpj(v: unknown): string | null {
  const d = String(v ?? '').replace(/\D/g, '');
  return d.length === 11 || d.length === 14 ? d : null;
}

/**
 * Datas da planilha vêm em três formas: serial do Excel, `dd/mm/aaaa` e
 * `m/d/aa` (linhas digitadas com o Excel em locale americano — ex.: `11/25/25`
 * e `10/6/25` na mesma coluna).
 *
 * Regra: quem for > 12 desempata; quando os dois cabem em mês, assume-se o
 * padrão brasileiro (dd/mm) mas a linha é marcada `ambigua` — sem isso uma
 * parte do histórico entraria com a data trocada e ninguém perceberia.
 */
export function parseDataPlanilha(v: unknown): { iso: string | null; ambigua: boolean } {
  const bruto = norm(v);
  if (!bruto) return { iso: null, ambigua: false };

  // Serial do Excel (dias desde 30/12/1899). O intervalo evita confundir com
  // um ano digitado solto ("2026").
  if (/^\d{5}$/.test(bruto)) {
    const serial = Number(bruto);
    if (serial > 20000 && serial < 60000) {
      const ms = Date.UTC(1899, 11, 30) + serial * 86400000;
      return { iso: new Date(ms).toISOString().slice(0, 10), ambigua: false };
    }
  }

  const iso = bruto.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return { iso: `${iso[1]}-${iso[2]}-${iso[3]}`, ambigua: false };

  const m = bruto.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2,4})$/);
  if (!m) return { iso: null, ambigua: false };

  const a = Number(m[1]);
  const b = Number(m[2]);
  let ano = Number(m[3]);
  if (ano < 100) ano += 2000;

  let dia: number, mes: number, ambigua = false;
  if (a > 12 && b <= 12) { dia = a; mes = b; }
  else if (b > 12 && a <= 12) { dia = b; mes = a; }
  else if (a > 12 && b > 12) return { iso: null, ambigua: false };
  else { dia = a; mes = b; ambigua = true; }

  if (mes < 1 || mes > 12 || dia < 1 || dia > 31) return { iso: null, ambigua: false };
  const d = new Date(Date.UTC(ano, mes - 1, dia));
  if (d.getUTCMonth() !== mes - 1 || d.getUTCDate() !== dia) return { iso: null, ambigua: false };
  return { iso: d.toISOString().slice(0, 10), ambigua };
}

/**
 * Número solto de dentro de texto. A planilha mistura os dois separadores
 * decimais na MESMA coluna — `R$ 1,420.81` (americano) convive com
 * `2.234,41` (brasileiro) —, então o separador decimal é o ÚLTIMO `,` ou `.`
 * que aparecer, e só quando sobram 1 ou 2 dígitos depois dele.
 */
export function parseNumeroTexto(v: unknown): number | null {
  const bruto = norm(v);
  if (!bruto) return null;
  if (/^(ZERADO|ZERO|SEM SALDO)$/i.test(semAcento(bruto))) return 0;

  const m = bruto.match(/-?[\d.,]*\d/);
  if (!m) return null;
  let s = m[0];

  const sep = Math.max(s.lastIndexOf(','), s.lastIndexOf('.'));
  const casas = sep >= 0 ? s.length - sep - 1 : 0;

  if (sep >= 0 && casas >= 1 && casas <= 2) {
    s = s.slice(0, sep).replace(/[.,]/g, '') + '.' + s.slice(sep + 1);
  } else {
    s = s.replace(/[.,]/g, '');
  }
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

/**
 * "COM OU SEM TERMO DE RESCISÃO" é a coluna mais limpa da planilha (6 valores
 * distintos em 202 linhas) e várias linhas carregam a data de emissão dentro
 * do próprio texto: "TEM TERMO DE DISTRATO EMITIDO DIA 21/01/2026".
 */
export function parseDistratoTexto(v: unknown): {
  distrato: DistratoInformado | null; data: string | null; ref: string | null;
} {
  const bruto = norm(v);
  if (!bruto) return { distrato: null, data: null, ref: null };
  const k = chave(bruto);

  const mData = bruto.match(/(\d{1,2}[/.-]\d{1,2}[/.-]\d{2,4})/);
  const data = mData ? parseDataPlanilha(mData[1]).iso : null;

  if (/AGUARDANDO/.test(k)) return { distrato: 'aguardando', data, ref: bruto };
  if (/(NAO TEM|SEM TERMO|SEM DISTRATO|NAO POSSUI|NAO HOUVE)/.test(k)) {
    return { distrato: 'nao', data: null, ref: null };
  }
  if (/(TEM TERMO|COM TERMO|TERMO EMITIDO|DISTRATO EMITIDO|ASSINADO|EMITIDO)/.test(k)) {
    return { distrato: 'sim', data, ref: bruto };
  }
  if (/^(SIM|S)$/.test(k)) return { distrato: 'sim', data, ref: null };
  if (/^(NAO|N)$/.test(k)) return { distrato: 'nao', data: null, ref: null };
  return { distrato: null, data, ref: bruto };
}

/**
 * MOTIVO é texto livre — 169 valores distintos em 202 linhas, tudo em frase.
 * A classificação é por palavra-chave, em ordem de especificidade, e o
 * resultado é SUGESTÃO: a tela mostra para o usuário confirmar antes de gravar,
 * e o texto original vai inteiro para `motivo_detalhe` de qualquer forma.
 *
 * `confianca: 'baixa'` = nenhuma regra bateu, caiu no default 'outro'.
 */
const REGRAS_MOTIVO: Array<{ re: RegExp; motivo: MotivoCancelamento }> = [
  // Geração própria / concorrente vira 'outro': não é insatisfação com a Vigor
  // nem inadimplência, e a categoria dedicada não existe no CHECK da tabela.
  { re: /(PLACA|PAINEL|PAINEIS|ENERGIA SOLAR|FOTOVOLT|OUTRA EMPRESA|CONCORRENT|ASSESSORIA DE ENERGIA|PATROCINADOR)/, motivo: 'outro' },
  { re: /(INADIMPL|NAO PAG|NAO ESTA PAGANDO|DEBITO|DIVIDA|ATRASO|FALTA DE PAGAMENTO)/, motivo: 'inadimplencia' },
  { re: /(MUDAN|MUDOU|MUDEI|OUTRO ESTADO|DE CIDADE|TITULARIDADE|FOI EMBORA|DESOCUP|VENDEU O IMOVEL|VENDEU A CASA)/, motivo: 'mudanca_endereco' },
  { re: /(INSATISF|DESCONTO|COMPENSA|VANTAJ|AUMENTOU|VALOR ALTO|CARO|RECLAM|NAO GOSTOU|INSUFICIENTE|POUCO)/, motivo: 'insatisfacao' },
  { re: /(DESEMPREG|SEM CONDIC|FINANCEIR|APERTO|ORCAMENTO)/, motivo: 'financeiro' },
];

export function classificarMotivo(texto: unknown): { motivo: MotivoCancelamento; confianca: 'alta' | 'baixa' } {
  const k = chave(texto);
  if (!k) return { motivo: 'outro', confianca: 'baixa' };
  for (const r of REGRAS_MOTIVO) if (r.re.test(k)) return { motivo: r.motivo, confianca: 'alta' };
  return { motivo: 'outro', confianca: 'baixa' };
}

const STATUS_POR_CHAVE: Record<string, StatusSolicitacaoCancelamento> = {
  ABERTA: 'aberta', ABERTO: 'aberta',
  'EM ANDAMENTO': 'em_andamento', ANDAMENTO: 'em_andamento', EM_ANDAMENTO: 'em_andamento',
  CONCLUIDA: 'concluida', CONCLUIDO: 'concluida', FINALIZADO: 'concluida', FINALIZADA: 'concluida', CANCELADA: 'concluida',
  DESISTENCIA: 'cancelada', DESISTIU: 'cancelada',
};

export function parseStatusTexto(v: unknown, padrao: StatusSolicitacaoCancelamento): StatusSolicitacaoCancelamento {
  const k = chave(v);
  return k ? (STATUS_POR_CHAVE[k] ?? padrao) : padrao;
}

/** Nome de vendedor para casar com `profiles.full_name` sem tropeçar em acento/caixa. */
export function chaveNome(v: unknown): string {
  return chave(v).replace(/[^A-Z ]/g, ' ').replace(/\s+/g, ' ').trim();
}

// ---------------------------------------------------------------------------
// Casamento de colunas
// ---------------------------------------------------------------------------

export interface CamposBrutos {
  uc: string; cpfCnpj: string; cliente: string; dataSolicitacao: string; motivo: string;
  detalhe: string; vendedor: string; distrato: string; distratoRef: string; dataRetirada: string;
  multaValor: string; multaSituacao: string; debitos: string; saldo: string; status: string;
  observacao: string;
}

/**
 * Cabeçalho → campo canônico. Ordem importa: o primeiro padrão que casar
 * ganha, e alguns cabeçalhos são prefixo de outros ("SALDO" x "SALDO DE
 * CREDITOS NA FATURAS"). Erros de digitação da planilha estão incluídos de
 * propósito ("SOLICTAÇÃO", "INFOMAÇÕED").
 */
const PADROES_COLUNA: Array<[keyof CamposBrutos, RegExp]> = [
  ['uc', /^(UNIDADE CONSUMIDORA|UC$|UC |NUMERO DA UC)/],
  ['cpfCnpj', /^(CPF|CNPJ)/],
  ['cliente', /^(NOME DO CLIENTE|CLIENTE|NOME\/RAZAO|NOME)/],
  ['dataSolicitacao', /^DATA DA SOLIC/],
  ['detalhe', /^(DETALHE DO MOTIVO)/],
  ['motivo', /^MOTIVO/],
  ['vendedor', /^(VENDEDOR RESPONSAVEL|PRA QUEM FOI ENCAMINHADO|RESPONSAVEL)/],
  ['distratoRef', /^(N. \/ DATA DO TERMO|NO \/ DATA DO TERMO|NUMERO DO TERMO)/],
  ['distrato', /(TERMO DE RESCIS|SOBRE O DISTRATO|INFOMACOED SOBRE|^DISTRATO)/],
  ['dataRetirada', /^DATA DA RETIRADA|^DATA DE RETIRADA/],
  ['multaValor', /^VALOR DA MULTA/],
  ['multaSituacao', /^(ESTA PAGO|MULTA)/],
  ['debitos', /^(DEBITOS EM ABERTO|VALOR EM ABERTO)/],
  ['saldo', /^SALDO/],
  ['status', /^(STATUS|SITUACAO)/],
  ['observacao', /^(OBSERVACAO|INFORMACOES DO QUE FOI VERIFICADO|OBS)/],
];

export type MapaColunas = Partial<Record<keyof CamposBrutos, number>>;

export function mapearColunas(cabecalho: unknown[]): MapaColunas {
  const mapa: MapaColunas = {};
  cabecalho.forEach((celula, i) => {
    const k = chave(celula);
    if (!k) return;
    for (const [campo, re] of PADROES_COLUNA) {
      // Primeira ocorrência vence: várias abas repetem "SALDO"/"MES DE
      // REFERENCIA" em colunas diferentes, e a primeira é a que a equipe
      // preenchia de fato.
      if (mapa[campo] === undefined && re.test(k)) { mapa[campo] = i; return; }
    }
  });
  return mapa;
}

// ---------------------------------------------------------------------------
// Linha → registro canônico
// ---------------------------------------------------------------------------

export interface OpcoesParse {
  aba: string;
  /** Status atribuído quando a planilha não traz coluna de status. */
  statusPadrao?: StatusSolicitacaoCancelamento;
}

export function parseLinha(
  celulas: unknown[], mapa: MapaColunas, linha: number, opts: OpcoesParse,
): LinhaPlanilha | null {
  const get = (c: keyof CamposBrutos) => (mapa[c] === undefined ? '' : norm(celulas[mapa[c]!]));

  const ucBruta = get('uc');
  const ucNormalizada = normalizarUcTexto(ucBruta);
  const cliente = get('cliente');
  // Linha sem UC e sem cliente é separador/rodapé da planilha, não registro.
  if (!ucNormalizada && !cliente) return null;

  const avisos: string[] = [];
  const { iso: dataSolicitacao, ambigua } = parseDataPlanilha(get('dataSolicitacao'));
  // No modelo canônico o motivo já vem categorizado e o texto vai em "Detalhe";
  // na planilha histórica só existe a coluna livre. Aceita os dois.
  const motivoBruto = get('motivo');
  const detalhe = get('detalhe');
  const classificado = classificarMotivoCategoria(motivoBruto);
  const { motivo, confianca } = classificado ?? classificarMotivo(motivoBruto);
  const motivoTexto = detalhe || (classificado ? null : motivoBruto) || null;
  const dist = parseDistratoTexto(get('distrato'));
  const refExplicita = get('distratoRef');

  if (!ucNormalizada) avisos.push('Sem número de UC.');
  else if (ucNormalizada.length < 6) avisos.push(`Número de UC curto demais ("${ucBruta}") — provável erro de digitação.`);
  if (!dataSolicitacao) avisos.push('Data da solicitação ausente ou ilegível.');
  if (ambigua) avisos.push('Data ambígua (dia e mês ≤ 12) — assumido dd/mm.');
  if (confianca === 'baixa' && motivoBruto) avisos.push('Motivo não reconhecido — classificado como "Outro".');

  return {
    aba: opts.aba,
    linha,
    ucBruta,
    ucNormalizada,
    cpfCnpj: normalizarCpfCnpj(get('cpfCnpj')),
    cliente,
    dataSolicitacao,
    dataAmbigua: ambigua,
    motivoTexto,
    motivoCategoria: motivo,
    motivoConfianca: confianca,
    vendedorNome: get('vendedor') || null,
    distrato: dist.distrato,
    distratoData: dist.data,
    distratoRef: refExplicita || dist.ref,
    saldoKwh: parseNumeroTexto(get('saldo')),
    debitosValor: parseNumeroTexto(get('debitos')),
    multaValor: parseNumeroTexto(get('multaValor')),
    multaSituacao: get('multaSituacao') || null,
    dataRetiradaRateio: parseDataPlanilha(get('dataRetirada')).iso,
    status: parseStatusTexto(get('status'), opts.statusPadrao ?? 'concluida'),
    observacao: get('observacao') || null,
    avisos,
  };
}

/**
 * Reimportação do arquivo que a própria tela exportou: a coluna "Motivo
 * (categoria)" já vem com o rótulo fechado, e passar isso pelo classificador
 * de texto livre erraria (ex.: "Inadimplência" casa a regra de insatisfação
 * por causa de nenhuma palavra-chave; pior, "Outro" nunca casaria nada).
 * Devolve null quando o texto não é um rótulo conhecido.
 */
function classificarMotivoCategoria(texto: string): { motivo: MotivoCancelamento; confianca: 'alta' } | null {
  const rotulos: Record<string, MotivoCancelamento> = {
    INADIMPLENCIA: 'inadimplencia',
    INSATISFACAO: 'insatisfacao',
    'MUDANCA DE ENDERECO': 'mudanca_endereco',
    'FINANCEIRO (CLIENTE)': 'financeiro',
    OUTRO: 'outro',
  };
  const m = rotulos[chave(texto)];
  return m ? { motivo: m, confianca: 'alta' } : null;
}

/** Uma aba inteira (matriz crua, cabeçalho na primeira linha) → registros. */
export function parseAba(nome: string, matriz: unknown[][], opts?: Omit<OpcoesParse, 'aba'>): LinhaPlanilha[] {
  if (matriz.length < 2) return [];
  const mapa = mapearColunas(matriz[0]);
  if (mapa.uc === undefined) return [];
  const out: LinhaPlanilha[] = [];
  matriz.slice(1).forEach((celulas, i) => {
    const r = parseLinha(celulas, mapa, i + 2, { aba: nome, ...opts });
    if (r) out.push(r);
  });
  return out;
}

/**
 * A mesma UC aparece em abas diferentes (solicitação repetida ao longo dos
 * meses) e às vezes duplicada dentro da própria aba. Colapsa por UC + data:
 * fica a linha MAIS COMPLETA (mais campos preenchidos), e a existência da
 * descartada vira aviso, para a equipe saber que houve colapso.
 *
 * A chave inclui a data porque duas solicitações da mesma UC em meses
 * diferentes são eventos distintos e legítimos — só o mesmo par UC+data é
 * duplicata. É a mesma chave do índice único criado na migration.
 */
export function deduplicar(linhas: LinhaPlanilha[]): { linhas: LinhaPlanilha[]; removidas: number } {
  const preenchidos = (l: LinhaPlanilha) =>
    [l.dataSolicitacao, l.motivoTexto, l.vendedorNome, l.distrato, l.saldoKwh,
     l.debitosValor, l.multaValor, l.multaSituacao, l.dataRetiradaRateio, l.observacao]
      .filter(v => v !== null && v !== undefined && v !== '').length;

  const porChave = new Map<string, LinhaPlanilha>();
  let removidas = 0;
  for (const l of linhas) {
    const k = `${l.ucNormalizada}|${l.dataSolicitacao ?? ''}`;
    const atual = porChave.get(k);
    if (!atual) { porChave.set(k, l); continue; }
    removidas++;
    const vencedora = preenchidos(l) > preenchidos(atual) ? l : atual;
    const perdedora = vencedora === l ? atual : l;
    vencedora.avisos = Array.from(new Set([
      ...vencedora.avisos,
      `Linha duplicada na planilha (${perdedora.aba}, linha ${perdedora.linha}) — mantida a mais completa.`,
    ]));
    porChave.set(k, vencedora);
  }
  return { linhas: [...porChave.values()], removidas };
}
